import { LoadingSpinnerContainer } from 'component/progress/loading-spinner';
import { useLanguage } from 'component/translation';
import { useNeedsProfileSetup } from 'data/me';
import { Redirect, router, useLocalSearchParams } from 'expo-router';
import { useEffect, useState } from 'react';
import { OnboardingScreen } from 'structure/login';
import { useInstanceKey } from 'util/datastore';

/**
 * An alias for the signed-in user's own profile, which is also where they set
 * it up.
 *
 * - Until setup is done, `/profile` shows onboarding.
 * - Once setup is saved and the profile reads back as set up, `/profile` goes
 *   back to the page that sent the user here, which by then reads the profile
 *   as set up too. With nothing to go back to, `/profile` resolves to the
 *   user's profile like any visit would.
 * - Otherwise `/profile` redirects to the user's own profile page.
 */
export default function ProfileRedirectScreen() {
    const { siloKey } = useLocalSearchParams<'/[siloKey]/profile'>();
    const profileKey = useInstanceKey();
    const locale = useLanguage();
    const needsSetup = useNeedsProfileSetup({ siloKey, profileKey, locale });
    const [setupSaved, setSetupSaved] = useState(false);
    const shouldGoBack = setupSaved && needsSetup === false && router.canGoBack();

    useEffect(() => {
        if (shouldGoBack) {
            router.back();
        }
    }, [shouldGoBack]);

    if (needsSetup === undefined || shouldGoBack) {
        return <LoadingSpinnerContainer />;
    }

    if (!needsSetup) {
        return (
            <Redirect
                href={{
                    pathname: '/[siloKey]/profile/[instanceKey]',
                    params: {
                        siloKey,
                        instanceKey: profileKey,
                    },
                }}
            />
        );
    }

    return <OnboardingScreen onComplete={() => setSetupSaved(true)} />;
}
